import type { IHierarchyViewSelectItem } from 'components/HierarchyViewSelect/HierarchyViewSelect';
import { TooltipWrapper } from 'components/TooltipWrapper';
import { ChoiceGroup, Grid, GridItem, IconAddToComparison, Button } from 'uikit';

import type React from 'react';
import { useCallback, useMemo } from 'react';

import { ConstructionObjectCombobox, ConstructionSubObjectCombobox } from '../ConstructionObjectCombobox';

import type { IWorkOrderFilterFormProps } from './types';
import { levelItems, WorkOrderFilterLevelEnum } from './types';

export const WorkOrderFilter: React.FC<IWorkOrderFilterFormProps> = (props) => {
    const {
        constructionTypeId,
        defaultValues,
        onChange,
        workOrderObjects,
        onActsKc2AnalyticsOpen,
        isActsKc2AnalyticsEnabled,
        actsKc2AnalyticsButtonTooltip,
    } = props;

    const handleObjectChange = useCallback(
        ({ value }) => {
            const filters = { objectId: value?.id, subObjectId: undefined };

            onChange(filters);
        },
        [onChange],
    );

    const handleSubObjectChange = useCallback(
        (selectedItem: IHierarchyViewSelectItem | undefined) => {
            onChange({ subObjectId: selectedItem?.id });
        },
        [onChange],
    );

    const handleLevelChange = useCallback(
        ({ value }: { value: (typeof levelItems)[0] }) => {
            onChange({
                level: value,
            });
        },
        [onChange],
    );

    const levelValue = useMemo(() => {
        if (defaultValues?.level) {
            return levelItems.find((level) => level.value === defaultValues?.level?.value);
        }

        return levelItems[0];
    }, [defaultValues?.level]);

    const isSummaryChapter = useMemo(
        () => defaultValues?.level?.value === WorkOrderFilterLevelEnum.TOTALS,
        [defaultValues?.level?.value],
    );

    const objectValue = useMemo(() => {
        if (isSummaryChapter) {
            return undefined;
        }

        return defaultValues?.objectId;
    }, [defaultValues?.objectId, isSummaryChapter]);

    const subObjectValue = useMemo(() => {
        if (isSummaryChapter) {
            return undefined;
        }

        return defaultValues?.subObjectId;
    }, [defaultValues?.subObjectId, isSummaryChapter]);

    const onActsKc2AnalyticsOpenLocal = useCallback(
        (e) => {
            e.stopPropagation();
            e.preventDefault();

            onActsKc2AnalyticsOpen?.();
        },
        [onActsKc2AnalyticsOpen],
    );

    return (
        <form>
            <Grid cols={6} gap="xl" rowGap="xl" colGap="xl">
                <GridItem col={2}>
                    <ConstructionObjectCombobox
                        constructionTypeId={constructionTypeId}
                        objectId={objectValue}
                        onChange={handleObjectChange}
                        disabled={isSummaryChapter}
                        workOrderObjects={workOrderObjects}
                    />
                </GridItem>
                <GridItem col={2}>
                    <ConstructionSubObjectCombobox
                        constructionTypeId={constructionTypeId}
                        objectId={defaultValues?.objectId}
                        subObjectId={subObjectValue}
                        onChange={handleSubObjectChange}
                        disabled={isSummaryChapter}
                        workOrderObjects={workOrderObjects}
                    />
                </GridItem>
                <GridItem col={2}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <ChoiceGroup
                            value={levelValue}
                            items={levelItems}
                            onChange={handleLevelChange}
                            getItemLabel={(item) => item?.label}
                            name="level"
                            size="s"
                            view="ghost"
                            multiple={false}
                        />
                        {onActsKc2AnalyticsOpen && (
                            <TooltipWrapper
                                direction="upCenter"
                                message={actsKc2AnalyticsButtonTooltip}
                                isHidden={!actsKc2AnalyticsButtonTooltip}
                                style={{ width: 32, marginLeft: 16 }}
                            >
                                <Button
                                    onClick={onActsKc2AnalyticsOpenLocal}
                                    iconSize="s"
                                    size="s"
                                    iconLeft={IconAddToComparison}
                                    view="secondary"
                                    style={{
                                        pointerEvents: !isActsKc2AnalyticsEnabled ? 'none' : 'auto',
                                    }}
                                    disabled={!isActsKc2AnalyticsEnabled}
                                    onlyIcon
                                />
                            </TooltipWrapper>
                        )}
                    </div>
                </GridItem>
            </Grid>
        </form>
    );
};

WorkOrderFilter.displayName = 'WorkOrderFilterForm';
